import { useState, useEffect } from "react";
import { db } from "../../backend/firebaseConfig";
import { doc, getDoc, setDoc, runTransaction, onSnapshot } from "firebase/firestore";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";

// Import Atoms
import SplashScreen from "../../components/UI/atoms/splash/SplashScreen"

// Imports MUI Components
import {  Box,
          Stack,
          Grid,
          Tooltip,
          Snackbar,
          Alert as MuiAlert,
          Dialog,
          DialogTitle,
          DialogContent,
          DialogActions,
          IconButton,
          CircularProgress,
          Typography
        } from "@mui/material"
import { Close } from "@mui/icons-material";

// Import Organisms
import Sidebar from "../../components/UI/organisms/sidebar/Sidebar"

// Import Functions
import { fetchUserDetails } from "../../components/functions/fetchUserDetails";
import { notifyFirstUser } from "../../components/functions/notifyFirstUser";
import { redirectIfNotLoggedIn } from "../../components/functions/redirectIfNotLoggedIn";

import Header from "../../components/UI/organisms/header/logged-in/HeaderLoggedIn";
import Search from "../../components/UI/molecules/search/Search";
import Text from "../../components/UI/atoms/text/Text";
import Icon from "../../components/UI/atoms/icon/Icon";
import Fab from "../../components/UI/atoms/fab/Fab";

const Services = () => {
  const [services, setServices] = useState([]);
  const [userDetails, setUserDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingService, setEditingService] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newService, setNewService] = useState({
    name: "",
    price: "",
    category: "Basic Services",
    duration: "",
    definition: ""
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [showAddUpdateSnackbar, setShowAddUpdateSnackbar] = useState(false);
  const [addUpdateSnackbarMessage, setAddUpdateSnackbarMessage] = useState("");
  const [showDeleteSnackbar, setShowDeleteSnackbar] = useState(false);
  const [deleteSnackbarMessage, setDeleteSnackbarMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({
    name: "",
    price: "",
    duration: "",
    definition: "",
  });
  const [isRemovingService, setIsRemovingService] = useState(false);
  const [isCancellingService, setIsCancellingService] = useState(false);

  const [showCustomAlert, setShowCustomAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("")
  const [alertImage, setAlertImage] = useState("");
  const [alertButton, setAlertButton] = useState("")
  const [alertButtonType, setAlertButtonType] = useState("false")
  const [alertLink, setAlertLink] = useState("")

  // Store unsubscribe functions for manual cleanup
  const [unsubscribeFunctions, setUnsubscribeFunctions] = useState([]);

  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();
  const userRole = location.state?.userRole || "DentistOwner";

  // Categories for dropdown
  const categories = [
    "Basic Services",
    "Denture",
    "Denture Repair", 
    "Extraction & Surgery",
    "Orthodontics",
    "Cosmetic",
    "Retainer",
    "TMD Treatment"
  ];

  /** Positive whole minutes only; optional empty. Rejects 0, negatives, leading zeros. */
  const normalizeDurationInput = (raw, previousValue) => {
    if (raw === "" || raw == null) return "";
    const trimmed = String(raw).trim();
    if (trimmed === "") return "";
    if (/^0/.test(trimmed)) return previousValue;
    if (/^-/.test(trimmed)) return previousValue;
    const n = parseInt(trimmed, 10);
    if (Number.isNaN(n)) return previousValue;
    if (n < 1) return previousValue;
    return String(n);
  };

  const formatDurationFromStored = (stored) => {
    if (stored == null || String(stored).trim() === "") return "";
    const digits = String(stored).replace(/\D/g, "");
    if (!digits || /^0/.test(digits)) return "";
    const n = parseInt(digits, 10);
    return Number.isFinite(n) && n >= 1 ? String(n) : "";
  };

  // UseEffects
  useEffect(() => {
    const details = fetchUserDetails(setUserDetails);
    const redirect = redirectIfNotLoggedIn(setShowCustomAlert);
    const notify = notifyFirstUser();
    return () => {
      details && details();
      redirect && redirect();
      notify && notify();
    };
  }, []);

  // Fetch user details for sidebar profile
  useEffect(() => {
    const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const snapshot = await getDoc(userRef);
        if (snapshot.exists()) {
          setUserDetails(snapshot.data());
        }
      }
    });
    return () => {
      authUnsubscribe();
    };
  }, [auth]);

  // Load services from Firestore per dentist with real-time updates
  useEffect(() => {
    if (!userDetails || !auth.currentUser) return;
    
    // Only load services for DentistOwner or AssociateDentist
    if (userDetails.role !== "DentistOwner" && userDetails.role !== "AssociateDentist") {
      return;
    }

    const dentistUID = auth.currentUser.uid;
    const servicesRef = doc(db, "services", dentistUID);
    
    const unsubscribe = onSnapshot(
      servicesRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setServices(snapshot.data().servicesList || []);
        } else {
          // If no services in Firestore, try to get from old structure first, then default
          (async () => {
            try {
              // First, try to get from old structure
              const oldServicesRef = doc(db, "services", "servicesList");
              const oldServicesSnap = await getDoc(oldServicesRef);
              
              let servicesToUse = [];
              if (oldServicesSnap.exists() && oldServicesSnap.data().servicesList) {
                servicesToUse = oldServicesSnap.data().servicesList;
              } else {
                // Fallback to default services
                const { servicesList } = await import("../../components/UI/organisms/list/ServicesList");
                servicesToUse = servicesList;
              }
              
              // Add duration field to services if missing
              const servicesWithDuration = servicesToUse.map(service => ({
                ...service,
                duration: service.duration || ""
              }));
              
              setServices(servicesWithDuration);
              // Save services to Firestore for this dentist
              await setDoc(servicesRef, { servicesList: servicesWithDuration });
            } catch (error) {
              console.error("Error loading services:", error);
            }
          })();
        }
      },
      (error) => {
        // Silently handle permission errors during sign-out
        if (error.code === 'permission-denied') {
          console.log('Services listener stopped due to sign-out');
          return;
        }
        console.error("Error loading services:", error);
        // Fallback to default services on error
        (async () => {
          try {
            const { servicesList } = await import("../../components/UI/organisms/list/ServicesList");
            setServices(servicesList);
          } catch (importError) {
            console.error("Error loading default services:", importError);
          }
        })();
      }
    );
    
    return () => unsubscribe();
  }, [userDetails, auth]);

  // Save services to Firestore per dentist
  // Note: State updates are handled automatically by onSnapshot listener
  const saveServices = async (updatedServices) => {
    try {
      if (!auth.currentUser) {
        console.error("No authenticated user");
        return false;
      }
      const dentistUID = auth.currentUser.uid;
      const servicesRef = doc(db, "services", dentistUID);
      await setDoc(servicesRef, { servicesList: updatedServices });
      return true;
    } catch (error) {
      console.error("Error saving services:", error);
      return false;
    }
  };

  // Format price with peso sign and commas
  const formatPrice = (price) => {
    if (!price) return "";
    
    // Remove existing peso sign and commas for processing
    let cleanPrice = price.replace(/₱/g, '').replace(/,/g, '');
    
    // If it's a range (contains "-"), format both parts
    if (cleanPrice.includes('-')) {
      const parts = cleanPrice.split('-');
      const formattedParts = parts.map(part => {
        const num = parseFloat(part.trim());
        if (isNaN(num)) return part.trim();
        return num.toLocaleString('en-US');
      });
      return `₱${formattedParts.join('-')}`;
    }
    
    // If it's a single number
    const num = parseFloat(cleanPrice);
    if (isNaN(num)) return price; // Return original if not a number
    
    return `₱${num.toLocaleString('en-US')}`;
  };

  /** All four fields required for add/edit save: name, price, duration (minutes ≥ 1), definition. */
  const isServiceFieldsComplete = (data) => {
    if (!data) return false;
    const name = (data.name || "").trim();
    const price = (data.price || "").trim();
    const durTrim = (data.duration || "").trim();
    const definition = (data.definition || "").trim();
    if (!name || !price || !durTrim || !definition) return false;
    const d = parseInt(durTrim, 10);
    if (
      !Number.isFinite(d) ||
      d < 1 ||
      /^0/.test(durTrim) ||
      /[.-]/.test(durTrim)
    ) {
      return false;
    }
    const cleanPrice = price.replace(/₱/g, "").replace(/,/g, "").trim();
    if (!cleanPrice || Number.isNaN(parseFloat(cleanPrice))) return false;
    return true;
  };

  // Add new service
  const handleAddService = async () => {
    const errors = { name: "", price: "", duration: "", definition: "" };
    let hasErrors = false;

    const trimmedName = newService.name.trim();
    const normalizedName = trimmedName.toLowerCase();

    if (!trimmedName) {
      errors.name = "Service name is required";
      hasErrors = true;
    }

    if (!newService.price.trim()) {
      errors.price = "Price is required";
      hasErrors = true;
    }

    const durTrim = (newService.duration || "").trim();
    if (!durTrim) {
      errors.duration = "Duration is required";
      hasErrors = true;
    } else {
      const d = parseInt(durTrim, 10);
      if (
        !Number.isFinite(d) ||
        d < 1 ||
        /^0/.test(durTrim) ||
        /[.-]/.test(durTrim)
      ) {
        errors.duration = "Duration must be a whole number of minutes (1 or greater).";
        hasErrors = true;
      }
    }

    if (!newService.definition.trim()) {
      errors.definition = "Definition is required";
      hasErrors = true;
    }

    // Check for duplicate service names (case-insensitive)
    if (!hasErrors) {
      const nameLower = trimmedName.toLowerCase();
      const exists = services.some(
        (svc) => (svc.name || "").toLowerCase().trim() === nameLower
      );

      if (exists) {
        errors.name = `${trimmedName} is already in the list.`;
        hasErrors = true;
      }
    }

    if (hasErrors) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({ name: "", price: "", duration: "", definition: "" });

    const serviceToAdd = {
      name: trimmedName,
      price: formatPrice(newService.price.trim()),
      category: newService.category,
      duration: newService.duration.trim(),
      definition: newService.definition.trim()
    };

    const nowIso = new Date().toISOString();

    try {
      if (!auth.currentUser) {
        throw new Error("No authenticated user");
      }
      const dentistUID = auth.currentUser.uid;
      await runTransaction(db, async (transaction) => {
        const servicesRef = doc(db, "services", dentistUID);

        // Fetch current services list
        const servicesSnap = await transaction.get(servicesRef);
        const currentServices = servicesSnap.exists()
          ? servicesSnap.data().servicesList || []
          : [];

        // Check for duplicates in current list (case-insensitive)
        const exists = currentServices.some(
          (svc) => (svc.name || "").toLowerCase().trim() === normalizedName
        );
        if (exists) {
          throw new Error("DUPLICATE_SERVICE");
        }

        const updatedServices = [...currentServices, serviceToAdd];
        transaction.set(servicesRef, { servicesList: updatedServices });
      });

      const serviceName = serviceToAdd.name;
      setNewService({ name: "", price: "", category: "Basic Services", duration: "", definition: "" });
      setValidationErrors({ name: "", price: "", duration: "", definition: "" });
      setShowAddForm(false);
      
      // Show success snackbar
      setAddUpdateSnackbarMessage(`"${serviceName}" has been added successfully!`);
      setShowAddUpdateSnackbar(true);
    } catch (error) {
      if (error?.message === "DUPLICATE_SERVICE") {
        setValidationErrors((prev) => ({
          ...prev,
          name: `${trimmedName} is already in the list.`,
        }));
        return;
      }

      console.error("Error adding service:", error);
    }
  };

  // Update existing service
  const handleUpdateService = async (index, updatedService) => {
    if (!isServiceFieldsComplete(updatedService)) {
      alert("Please fill in service name, price, duration, and definition.");
      return;
    }

    const durTrim = (updatedService.duration || "").trim();
    const d = parseInt(durTrim, 10);
    const durationToSave = String(d);

    const updatedServices = [...services];
    updatedServices[index] = {
      ...updatedServices[index],
      name: updatedService.name.trim(),
      price: formatPrice(updatedService.price.trim()),
      category: updatedService.category,
      duration: durationToSave,
      definition: updatedService.definition.trim()
    };

    const success = await saveServices(updatedServices);
    if (success) {
      const serviceName = updatedService.name.trim();
      setEditingService(null);

      // Show success snackbar
      setAddUpdateSnackbarMessage(`"${serviceName}" has been updated successfully!`);
      setShowAddUpdateSnackbar(true);
    }
  };

  // Handle delete button click
  const handleDeleteService = (index) => {
    setServiceToDelete({ index, service: services[index] });
    setShowDeleteConfirm(true);
  };

  // Handle actual deletion
  const confirmDelete = async () => {
    if (serviceToDelete && !isRemovingService) {
      setIsRemovingService(true);
      const serviceName = serviceToDelete.service.name;
      const normalizedName = (serviceName || "").toLowerCase().trim();
      const updatedServices = services.filter((_, i) => i !== serviceToDelete.index);
      const success = await saveServices(updatedServices);
      if (success) {
        setEditingService(null);
        setShowDeleteConfirm(false);
        setServiceToDelete(null);
        setIsRemovingService(false);
        
        // Show delete success snackbar
        setDeleteSnackbarMessage(`"${serviceName}" has been deleted successfully!`);
        setShowDeleteSnackbar(true);
      } else {
        setIsRemovingService(false);
      }
    }
  };

  // Handle cancel deletion
  const cancelDelete = () => {
    if (isRemovingService || isCancellingService) return;
    setIsCancellingService(true);
    setTimeout(() => {
      setShowDeleteConfirm(false);
      setServiceToDelete(null);
      setIsCancellingService(false);
    }, 300);
  };

  // Start editing a service
  const startEditing = (index) => {
    const row = services[index];
    setEditingService({
      index,
      data: {
        ...row,
        duration: formatDurationFromStored(row?.duration),
      },
    });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingService(null);
  };

  // Handle input changes for editing
  const handleEditChange = (field, value) => {
    if (field === "duration") {
      setEditingService((prev) => {
        const prevDur = prev?.data?.duration ?? "";
        const next = normalizeDurationInput(value, prevDur);
        return {
          ...prev,
          data: { ...prev.data, duration: next },
        };
      });
      return;
    }
    setEditingService((prev) => ({
      ...prev,
      data: { ...prev.data, [field]: value },
    }));
  };

  // Handle input changes for new service
  const handleNewServiceChange = (field, value) => {
    if (field === "duration") {
      setNewService((prev) => ({
        ...prev,
        duration: normalizeDurationInput(value, prev.duration ?? ""),
      }));
      if (validationErrors.duration) {
        setValidationErrors((prevE) => ({ ...prevE, duration: "" }));
      }
      return;
    }
    setNewService((prev) => ({ ...prev, [field]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Filter services based on search term
  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLogout = () => {
    signOut(auth).then(() => {
      navigate("/", { replace: true });
    });
  };

  const boxStylesContent = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: "#FDF9F3",
    // width: "100%"
  }

  const gridStylesContainer = {
    // padding: "20px",
    width: "100%",
    // backgroundColor: "blue",
    "& .MuiGrid-item": {
      display: "flex",
      justifyContent: "center",
      // backgroundColor: "blue",
    },
  }

  return (
    <div>
      {/* All Content */}
      <Box 
        sx={boxStylesContent}>

        {/* Sidebar */}
          <Box
            display={{
              xs: "none",
              sm: "none",
              md: "block",
              lg: "block",
              xl: "block",
            }}>
          <Sidebar
            fName={userDetails?.firstName}
            lName={userDetails?.lastName}
            email={userDetails?.email}
          />
          </Box>

          <Box
            sx={{
              display: {
                xs: "block",
                sm: "block",
                md: "none",
                lg: "none",
                xl: "none",
              }
            }}
          >
          <Fab
            label={!showAddForm ? "Add Service" : "Cancel"}
            place="top-end"
            destination="none"
            src={showAddForm ? require("../../assets/icons/close_light.png") : require("../../assets/icons/add.png")}
            open={false}
            onClick={() => setShowAddForm(!showAddForm)}/>
          </Box>

          {/*Grids */}
        <Grid
          container
          // rowSpacing={2}
          // columnSpacing={2}
          sx={{
            width: "100%",
            height: "100vh",
            // backgroundColor: "blue",
            "& .MuiGrid-item": {
              display: "flex",
              justifyContent: "center",
              // backgroundColor: "blue",
            },
          }}>

            {/* Main Content */}
            <Grid 
              size={{ 
                xs: 12, 
                sm: 12, 
                md: 12, 
                lg: 12, 
                xl: 12 
              }} 
              item
            >
            
              <Header
                count={2}
                page="Services"
                onAdd={() => setShowAddForm(true)}/>

              {/*Grids */}
              <Grid
                container
                rowSpacing={4}
                columnSpacing={2}
                sx={gridStylesContainer}
              >

            {/* Main Content */}
            <Grid 
              size={{ 
                xs: 12, 
                sm: 12, 
                md: 12, 
                lg: 12, 
                xl: 12 
              }} 
              item
              sx={{
                display: "flex",
                flexDirection: "column",
                // border: "1px solid black",
              // boxShadow: "-1px 5px 10px rgba(0,0,0,0.25)",
                height: "calc(100vh - 105px)", // 👈 fills height below the header (adjust 80px if header height changes)
                overflow: "hidden",
                mt: "0px",
              }}
            >

                  <Box
                    sx={{
                      // background: "#FEFEFE",
                      overflow: "auto",
                      padding: {
                        xs : "30px 0 5px 0",
                        sm : "30px 0 5px 0",
                        md : "30px 30px 5px 30px",
                        lg : "30px 30px 5px 30px",
                        xl : "30px 30px 5px 30px",
                      },
                      // boxShadow: "0 4px 20px rgba(0,0,0,0.20)",
                      // border: "1px solid black",
                      borderRadius: {
                        xs: "0",
                        sm: "0",
                        md: "0",
                        lg: "15px",
                        xl: "15px",
                      },
                      minHeight: 0, 
                      height: "100%",
                      display: "flex",
                      flexDirection: "column"
                    }}
                  >
                    
                    <Stack
                      direction={{
                        xs: "column",
                        sm: "row",
                        md: "row",
                        lg: "row",
                        xl: "row",
                      }}
                      padding={{
                        xs: "0 20px 20px 20px",
                        sm: "0 20px 20px 20px",
                        md: "0 0 20px 0",
                        lg: "0 0 20px 0",
                        xl: "0 0 20px 0",
                      }}
                      alignItems={{
                        xs: "none",
                        sm: "center",
                        md: "center",
                        lg: "center",
                        xl: "center",
                      }}
                      justifyContent="space-between"
                      spacing={{
                        xs: 2,
                        sm: 0,
                        md: 0,
                        lg: 0,
                        xl: 0,
                      }}
                      >
                        
                      <Stack
                        direction="column"
                        spacing={1}>

                          <Text
                            text="Manage Services"
                            variant="serviceTitleNoMargin"/>

                          <Text
                            text="Here you can manage the services of the clinic."
                            variant="serviceSubtitleNoMargin"/>

                      </Stack>

                      <Search
                          handleSearch={(e) => setSearchTerm(e.target.value)}
                          page="manageServices"
                          value={searchTerm}/>

                    </Stack>

                    {/* Add New Service Form */}
                    {showAddForm && (
                      <div style={{
                        background: "#f8f9fa",
                        borderRadius: "12px",
                        padding: "20px",
                        marginBottom: "24px",
                        border: "1px solid #e9ecef"
                      }}>
                        <h3 style={{ margin: "0 0 16px 0", color: "#3d342b" }}>Add New Service</h3>
                        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                          <div style={{ display: "flex", gap: "20px", alignItems: "flex-start", flexWrap: "wrap" }}>
                            <div style={{ flex: 2, minWidth: "200px" }}>
                              <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", color: "#666" }}>
                                Service Name <span style={{ color: '#f44336' }}>*</span>
                              </label>
                              <input
                                type="text"
                                value={newService.name}
                                onChange={(e) => handleNewServiceChange("name", e.target.value)}
                                placeholder="Enter service name"
                                style={{
                                  width: "100%",
                                  padding: "8px 12px",
                                  border: validationErrors.name ? "1px solid #dc3545" : "1px solid #ddd",
                                  borderRadius: "6px",
                                  fontSize: "14px"
                                }}
                              />
                              <div style={{ minHeight: "20px" }}>
                                {validationErrors.name && (
                                  <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>
                                    {validationErrors.name}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div style={{ flex: 1, minWidth: "150px" }}>
                              <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", color: "#666" }}>
                                Price <span style={{ color: '#f44336' }}>*</span>
                              </label>
                              <input
                                type="text"
                                value={newService.price}
                                onChange={(e) => handleNewServiceChange("price", e.target.value)}
                                placeholder="e.g., 1000 or 1000-3000"
                                style={{
                                  width: "100%",
                                  padding: "8px 12px",
                                  border: validationErrors.price ? "1px solid #dc3545" : "1px solid #ddd",
                                  borderRadius: "6px",
                                  fontSize: "14px"
                                }}
                              />
                              <div style={{ minHeight: "20px" }}>
                                {validationErrors.price && (
                                  <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>
                                    {validationErrors.price}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div style={{ flex: 1, minWidth: "150px" }}>
                              <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", color: "#666" }}>
                                Duration (minutes) <span style={{ color: '#f44336' }}>*</span>
                              </label>
                              <input
                                type="number"
                                min={1}
                                step={1}
                                value={newService.duration}
                                onChange={(e) => handleNewServiceChange("duration", e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "-" || e.key === "+" || e.key === "e" || e.key === "E") {
                                    e.preventDefault();
                                  }
                                }}
                                placeholder="Service Duration (in minutes)"
                                style={{
                                  width: "100%",
                                  padding: "8px 12px",
                                  border: validationErrors.duration ? "1px solid #dc3545" : "1px solid #ddd",
                                  borderRadius: "6px",
                                  fontSize: "14px"
                                }}
                              />
                              <div style={{ minHeight: "20px" }}>
                                {validationErrors.duration && (
                                  <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>
                                    {validationErrors.duration}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div style={{ flex: 1, minWidth: "180px" }}>
                              <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", color: "#666" }}>
                                Category
                              </label>
                              <select
                                value={newService.category}
                                onChange={(e) => handleNewServiceChange("category", e.target.value)}
                                style={{
                                  width: "100%",
                                  padding: "8px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "6px",
                                  fontSize: "14px"
                                }}
                              >
                                {categories.map(category => (
                                  <option key={category} value={category}>{category}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div style={{ width: "100%" }}>
                            <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", color: "#666" }}>
                              Definition <span style={{ color: '#f44336' }}>*</span>
                            </label>
                            <textarea
                              value={newService.definition}
                              onChange={(e) => handleNewServiceChange("definition", e.target.value)}
                              placeholder="Enter service definition/description"
                              rows={3}
                              style={{
                                width: "100%",
                                padding: "8px 12px",
                                border: validationErrors.definition ? "1px solid #dc3545" : "1px solid #ddd",
                                borderRadius: "6px",
                                fontSize: "14px",
                                resize: "vertical",
                                fontFamily: "inherit"
                              }}
                            />
                            <div style={{ minHeight: "20px" }}>
                              {validationErrors.definition && (
                                <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>
                                  {validationErrors.definition}
                                </div>
                              )}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                            <button
                              type="button"
                              onClick={handleAddService}
                              disabled={!isServiceFieldsComplete(newService)}
                              style={{
                                background: !isServiceFieldsComplete(newService) ? "#a5d6a7" : "#4CAF50",
                                color: "white",
                                border: "none",
                                padding: "8px 16px",
                                borderRadius: "6px",
                                cursor: !isServiceFieldsComplete(newService) ? "not-allowed" : "pointer",
                                fontSize: "14px",
                                opacity: !isServiceFieldsComplete(newService) ? 0.85 : 1,
                              }}
                            >
                              Add
                            </button>
                            <button
                              onClick={() => {
                                setShowAddForm(false);
                                setNewService({ name: "", price: "", category: "Basic Services", duration: "", definition: "" });
                                setValidationErrors({ name: "", price: "", duration: "", definition: "" });
                              }}
                              style={{
                                background: "#6c757d",
                                color: "white",
                                border: "none",
                                padding: "8px 16px",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontSize: "14px"
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <Box
                      sx={{
                      borderRadius: {
                        xs: "0",
                        sm: "0",
                        md: "0",
                        lg: "10px",
                        xl: "10px",
                      },
                        boxShadow: "-1px 5px 10px rgba(0,0,0,0.25)",
                        flex: 1,
                        minHeight: 0,
                        overflowY: "auto",
                        position: "relative"
                      }}
                    >

                      {/* Group services by category */}
                      {Object.entries(
                        filteredServices.reduce((acc, service) => {
                          if (!acc[service.category]) {
                            acc[service.category] = [];
                          }
                          acc[service.category].push(service);
                          return acc;
                        }, {})
                      ).map(([category, categoryServices]) => (
                        <Box key={category} className="service-category-header">
                          <Box
                            sx={{
                              position: "sticky",
                              top: 0,
                              zIndex: 10,
                              backgroundColor: "#2E2D2D",
                              color: "#FDF9F3",
                              padding: "20px",
                              borderBottom: "1px solid rgba(0,0,0,0.08)"
                            }}
                          >
                            <Text
                              text={category}
                              variant="serviceTableLable"/>

                          </Box>

                          {categoryServices.map((service, index) => {
                            const globalIndex = services.findIndex(s => s.name === service.name);
                            const isEditing = editingService && editingService.index === globalIndex;
                            
                            return (
                              <Box key={`${category}-${index}`} sx={{ p: 2, borderBottom: "1px solid #eee" }}>
                                <Stack
                                  direction="row">
                                {isEditing ? (
                                  // Edit mode
                                  <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>
                                    <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                                      <input
                                        type="text"
                                        value={editingService.data.name}
                                        onChange={(e) => handleEditChange("name", e.target.value)}
                                        placeholder="Service Name"
                                        style={{
                                          flex: 2,
                                          minWidth: "200px",
                                          padding: "8px 12px",
                                          border: "1px solid #ddd",
                                          borderRadius: "6px",
                                          fontSize: "14px"
                                        }}
                                      />
                                      <input
                                        type="text"
                                        value={editingService.data.price}
                                        onChange={(e) => handleEditChange("price", e.target.value)}
                                        placeholder="Price"
                                        style={{
                                          flex: 1,
                                          minWidth: "120px",
                                          padding: "8px 12px",
                                          border: "1px solid #ddd",
                                          borderRadius: "6px",
                                          fontSize: "14px"
                                        }}
                                      />
                                      <input
                                        type="number"
                                        min={1}
                                        step={1}
                                        value={editingService.data.duration || ""}
                                        onChange={(e) => handleEditChange("duration", e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === "-" || e.key === "+" || e.key === "e" || e.key === "E") {
                                            e.preventDefault();
                                          }
                                        }}
                                        placeholder="Service Duration (in minutes)"
                                        style={{
                                          flex: 1,
                                          minWidth: "120px",
                                          padding: "8px 12px",
                                          border: "1px solid #ddd",
                                          borderRadius: "6px",
                                          fontSize: "14px"
                                        }}
                                      />
                                      <select
                                        value={editingService.data.category}
                                        onChange={(e) => handleEditChange("category", e.target.value)}
                                        style={{
                                          flex: 1,
                                          minWidth: "140px",
                                          padding: "8px 12px",
                                          border: "1px solid #ddd",
                                          borderRadius: "6px",
                                          fontSize: "14px"
                                        }}
                                      >
                                        {categories.map(cat => (
                                          <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <textarea
                                      value={editingService.data.definition || ""}
                                      onChange={(e) => handleEditChange("definition", e.target.value)}
                                      placeholder="Definition/Description"
                                      rows={2}
                                      style={{
                                        width: "100%",
                                        padding: "8px 12px",
                                        border: "1px solid #ddd",
                                        borderRadius: "6px",
                                        fontSize: "14px",
                                        resize: "vertical",
                                        fontFamily: "inherit"
                                      }}
                                    />
                                    <div style={{ display: "flex", gap: "8px" }}>
                                      <button
                                        type="button"
                                        onClick={() => handleUpdateService(globalIndex, editingService.data)}
                                        disabled={!isServiceFieldsComplete(editingService.data)}
                                        style={{
                                          background: !isServiceFieldsComplete(editingService.data)
                                            ? "#9ab89f"
                                            : "#28a745",
                                          color: "white",
                                          border: "none",
                                          padding: "6px 12px",
                                          borderRadius: "4px",
                                          cursor: !isServiceFieldsComplete(editingService.data)
                                            ? "not-allowed"
                                            : "pointer",
                                          fontSize: "12px",
                                          opacity: !isServiceFieldsComplete(editingService.data) ? 0.9 : 1,
                                        }}
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={cancelEditing}
                                        style={{
                                          background: "#6c757d",
                                          color: "white",
                                          border: "none",
                                          padding: "6px 12px",
                                          borderRadius: "4px",
                                          cursor: "pointer",
                                          fontSize: "12px"
                                        }}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                                        ) : (
                                  // View mode
                                  <>
                                    {/*Grids */}
                                    <Grid
                                      container
                                      columnSpacing={1}
                                      sx={{
                                        width: "100%",
                                        alignItems: "center",
                                        "& .MuiGrid-item": {
                                          display: "flex",
                                          justifyContent: "center",
                                        },
                                      }}
                                    >

                                      {/* Main Content */}
                                      <Grid 
                                        size={{ 
                                          xs: 12, 
                                          sm: 12, 
                                          md: 12, 
                                          lg: 5, 
                                          xl: 4 
                                        }} 
                                        item
                                        sx={{
                                          alignItems: "center",
                                          borderRight: {
                                            xs: null, 
                                            sm: null, 
                                            md: null, 
                                            lg: "2px solid #2E2D2D", 
                                            xl: "2px solid #2E2D2D" 
                                          },
                                          transition: " 0.3s ease" 
                                        }} 
                                      >

                                        <Typography
                                          variant="historyDateTime"
                                          sx={{      
                                            fontWeight: {
                                              xs: "600", 
                                              sm: "600", 
                                              md: "600", 
                                              lg: "100", 
                                              xl: "100" 
                                            },
                                          }}>
                                            {service.name}
                                        </Typography>

                                      </Grid>
                                        
                                      {/* Main Content */}
                                      <Grid 
                                        size={{ 
                                          xs: 12, 
                                          sm: 12, 
                                          md: 12, 
                                          lg: 7, 
                                          xl: 8 
                                        }} 
                                        item
                                      >
                                        <Text
                                          text={service.price}
                                          variant="serviceLabels"/>
                                      
                                      </Grid>
                                      
                                    </Grid>
                                    
                                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                      >

                                      <Tooltip
                                              title="Edit"
                                              placement="top"
                                              arrow
                                            >
                                      <Box
                                        onClick={() => startEditing(globalIndex)}
                                        sx={{
                                          borderRadius: "50px",
                                          height: "40px",
                                          width: "40px",
                                          cursor: "pointer",
                                          ":hover": {
                                            backgroundColor: "rgba(46, 45, 45, 0.2)"
                                          }
                                        }}>
                                        <Icon
                                          src={require("../../assets/icons/edit_v2.png")}
                                          size="small"/>
                                      </Box>
                                      </Tooltip>

                                        <Tooltip
                                              title="Delete"
                                              placement="top"
                                              arrow
                                            >
                                      <Box
                                        onClick={() => handleDeleteService(globalIndex)}
                                        sx={{
                                          borderRadius: "50px",
                                          height: "40px",
                                          width: "40px",
                                          cursor: "pointer",
                                          ":hover": {
                                            backgroundColor: "rgba(46, 45, 45, 0.2)"
                                          }
                                        }}>
                                        <Icon
                                          src={require("../../assets/delete.png")}
                                          size="small"/>
                                      </Box>
                                      </Tooltip>
                                      </Stack>
                                    </div>
                                  </>
                                )}
                              </Stack>
                              </Box>
                            );
                          })}
                        </Box>
                      ))}
                    
                    </Box>
                  </Box>
                  

                </Grid>

                </Grid>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={showDeleteConfirm}
        onClose={(_, reason) => {
          if (reason === "backdropClick") {
            if (!isRemovingService && !isCancellingService) {
              cancelDelete();
            }
            return;
          }
          if (!isRemovingService && !isCancellingService) {
            cancelDelete();
          }
        }}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 2000, "& .MuiDialog-paper": { borderRadius: 3, backgroundColor: "#FFFFFF", paddingBottom: 2 } }}
        BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,0.6)" } }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            color: "#C94D34",
            fontSize: "22px",
            padding: "28px 32px 12px 32px",
            position: "relative",
          }}
        >
          Confirm Service Deletion
          <IconButton
            onClick={cancelDelete}
            disabled={isRemovingService || isCancellingService}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: "#6D6257",
              "&:hover": { backgroundColor: "rgba(109, 98, 87, 0.08)" },
            }}
          >
            {isCancellingService ? (
              <CircularProgress size={16} sx={{ color: "#6D6257" }} />
            ) : (
              <Close fontSize="small" />
            )}
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "0 32px 8px 32px" }}>
          <Stack spacing={2} alignItems="center" sx={{ textAlign: "center" }}>
            <Box sx={{ fontSize: 16, color: "#2C251E" }}>
              <span>Are you sure you want to delete </span>
              <span style={{ fontWeight: 700 }}>
                "{serviceToDelete?.service?.name || ""}"
              </span>
              <span>?</span>
            </Box>
            <Box sx={{ fontSize: 14, color: "rgba(44, 37, 30, 0.6)" }}>
              This action cannot be undone.
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ padding: "12px 32px 28px 32px", justifyContent: "center", gap: 2 }}>
          <button
            onClick={cancelDelete}
            disabled={isRemovingService || isCancellingService}
            style={{
              color: "#6D6257",
              border: "1px solid #6D6257",
              padding: "10px 22px",
              borderRadius: 20,
              fontSize: "15px",
              fontWeight: 600,
              minWidth: "120px",
              textTransform: "none",
              background: "transparent",
              cursor: "pointer",
            }}
          >
            {isCancellingService ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <CircularProgress size={16} sx={{ color: "#6D6257" }} /> Cancelling...
              </span>
            ) : (
              "No, Cancel"
            )}
          </button>
          <button
            onClick={confirmDelete}
            disabled={isRemovingService || isCancellingService}
            style={{
              background: (isRemovingService || isCancellingService) ? "#D3CBC1" : "#C94D34",
              color: (isRemovingService || isCancellingService) ? "#F5F1EC" : "#fff",
              padding: "10px 22px",
              borderRadius: 20,
              fontSize: "15px",
              fontWeight: 600,
              minWidth: "120px",
              textTransform: "none",
              border: "none",
              cursor: (isRemovingService || isCancellingService) ? "not-allowed" : "pointer",
            }}
          >
            {isRemovingService ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <CircularProgress size={16} sx={{ color: "#fff" }} /> Deleting...
              </span>
            ) : (
              "Yes, Delete"
            )}
          </button>
        </DialogActions>
      </Dialog>
          </Grid>

        </Grid>

       </Box>


    <SplashScreen
      isOpen={showCustomAlert}
      image={alertImage}
      button={alertButton}
      buttonType={alertButtonType}
      destination={alertLink}
      onClose={() => setShowCustomAlert(false)}/>

      {/* Add/Update Success Snackbar */}
      <Snackbar
        open={showAddUpdateSnackbar}
        autoHideDuration={5000}
        onClose={() => {
          setShowAddUpdateSnackbar(false);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => {
            setShowAddUpdateSnackbar(false);
          }}
          severity="success"
          variant="filled"
          icon={false}
          sx={{
            backgroundColor: "#558363",
            color: "#fff",
            fontWeight: 600,
            letterSpacing: 0.2,
            borderRadius: "12px",
            padding: "8px 16px",
            minWidth: "auto",
            width: "auto",
            boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center"
          }}
        >
          {addUpdateSnackbarMessage}
        </MuiAlert>
      </Snackbar>

      {/* Delete Success Snackbar */}
      <Snackbar
        open={showDeleteSnackbar}
        autoHideDuration={5000}
        onClose={() => {
          setShowDeleteSnackbar(false);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => {
            setShowDeleteSnackbar(false);
          }}
          severity="success"
          variant="filled"
          icon={false}
          sx={{
            backgroundColor: "#558363",
            color: "#fff",
            fontWeight: 600,
            letterSpacing: 0.2,
            borderRadius: "12px",
            padding: "8px 16px",
            minWidth: "auto",
            width: "auto",
            boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center"
          }}
        >
          {deleteSnackbarMessage}
        </MuiAlert>
      </Snackbar>
      
     </div>
   );
 };

export default Services;